import React, { ReactNode, useMemo } from 'react'

import { ThemeProvider } from '@mui/material'
import type { SxProps } from '@mui/material/styles'

import { getTheme } from 'themes'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Icon, { IconProps } from 'components_new/atoms/Icon'
import ListItemButton from 'components_new/atoms/List/ListItemButton'
import ListItemIcon from 'components_new/atoms/List/ListItemIcon'
import ListItemText from 'components_new/atoms/List/ListItemText'
import Skeleton from 'components_new/atoms/Skeleton'
import Tooltip, { TooltipProps } from 'components_new/atoms/Tooltip'

import GenericAvatar, {
  GenericAvatarProps
} from 'components_new/molecules/GenericAvatar'

export interface SelectButtonBaseProps {
  active?: boolean
  avatar?: GenericAvatarProps
  collapsed?: boolean
  disabled?: boolean
  focused?: boolean
  icon?: IconProps
  loading?: boolean
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  placeholder?: string
  size?: 'medium' | 'large'
  sx?: SxProps
  title?: ReactNode
  tooltipPlacement?: TooltipProps['placement']
  tooltipSlotProps?: TooltipProps['slotProps']
  tooltipTitle?: ReactNode
  tooltipValue?: ReactNode
  value: string | null
  valueMaxWidth?: number
}

const SelectButtonBase = React.forwardRef<
  HTMLDivElement,
  SelectButtonBaseProps
>((props, ref) => {
  const {
    active,
    avatar,
    collapsed,
    disabled,
    focused,
    icon,
    loading,
    onClick,
    placeholder,
    size,
    sx,
    title,
    tooltipPlacement = 'right',
    tooltipSlotProps,
    tooltipTitle,
    tooltipValue,
    value
  } = props

  const { t } = useTranslation()
  const theme = useMemo(() => {
    return getTheme(active ? 'dark' : 'light')
  }, [active])

  const invisibleOnCollapsedSx = {
    opacity: collapsed ? 0 : 1,
    transition: 'opacity 0.5s ease'
  }

  const noOverflowSx = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        ref={ref}
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          ...(focused && !active ? { borderColor: 'primary.main' } : null),
          ...sx
        }}
      >
        <Tooltip
          slotProps={tooltipSlotProps}
          title={
            collapsed ? (
              <>
                {tooltipTitle}
                <br />
                <b>{tooltipValue}</b>
              </>
            ) : undefined
          }
          placement={tooltipPlacement}
        >
          <ListItemButton
            dense={true}
            disabled={disabled || loading}
            onClick={onClick}
            sx={{ height: '100%', px: 1 }}
          >
            {avatar || icon ? (
              <ListItemIcon sx={{ minWidth: 28 }}>
                {avatar ? <GenericAvatar {...avatar} /> : null}
                {icon ? <Icon {...icon} sx={{ fontSize: '1.25rem' }} /> : null}
              </ListItemIcon>
            ) : null}
            <ListItemText
              primary={title}
              primaryTypographyProps={{ color: 'text.secondary' }}
              secondary={
                loading ? (
                  <Skeleton variant="text" />
                ) : (
                  value ?? placeholder ?? t('global', 'none')
                )
              }
              secondaryTypographyProps={{
                color: !value ? 'text.disabled' : 'text.primary',
                noWrap: true,
                variant: 'navigation'
              }}
              sx={{
                ...invisibleOnCollapsedSx,
                ...noOverflowSx,
                ...(size === 'medium' ? { my: 0 } : null)
              }}
            />
            <Icon
              name="UnfoldMore"
              sx={{
                color: 'action.active',
                fontSize: '1.25rem',
                ml: 1,
                ...invisibleOnCollapsedSx
              }}
            />
          </ListItemButton>
        </Tooltip>
      </Box>
    </ThemeProvider>
  )
})

SelectButtonBase.displayName = 'SelectButtonBase'

export default SelectButtonBase
